/**
 * AdsCampaignDetailPage — Tela de detalhe de uma campanha (/ads/campaigns/:id).
 *
 * Fonte da verdade do fluxo/estados:
 *   docs/execucao-projetos/_local/design/wireframes/2026-06-09-analise-campanha-isolada.md §2.2
 *
 * Contrato de API consumido:
 *   GET /ads/campaigns/:id?period=30d  → CampaignDetailResponse
 *
 * Estados honestos implementados:
 *   loading           → SkeletonGrid (proporcoes dos kpi-cards reais)
 *   ready             → grade de KPIs + sparklines + bloco CROSS
 *   ready-no-roas     → ROAS exibe "—" com explicacao inline (R-063)
 *   not-found         → campanha nao encontrada
 *   no-period-data    → campanha existe mas sem dado no periodo
 *   error             → mensagem + retry
 *
 * Invariantes:
 *   - ROAS sempre null (R-063) → "—" com explicacao honesta
 *   - null → "—" nunca "0"
 *   - campaign_insight null → bloco CROSS nao renderiza
 *   - FAB navega para /chat?context=campaign:[id]
 *
 * F2 — 2026-06-09
 */

import '../styles/screen-ads.css';
import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { Skeleton } from '../components/Skeleton';
import { Sparkline } from '../components/Sparkline';
import {
  fmtCurrency,
  fmtNumber,
  fmtPercent,
  statusLabel,
  statusCssClass,
  AdsRefPanel,
} from './AdsCampaignsListPage';
import type { CampaignItem, RefPanelData } from './AdsCampaignsListPage';

// ---------------------------------------------------------------------------
// Tipos de resposta de detalhe
// ---------------------------------------------------------------------------

interface CampaignSeries {
  readonly dates: readonly string[];
  readonly spend: ReadonlyArray<number | null>;
  readonly clicks: ReadonlyArray<number | null>;
  readonly cpc: ReadonlyArray<number | null>;
  readonly ctr: ReadonlyArray<number | null>;
}

type CampaignDetailItem = CampaignItem & {
  readonly series: CampaignSeries;
  readonly campaign_insight: string | null;
};

interface CampaignDetailResponse {
  readonly campaign: CampaignDetailItem;
  readonly source: {
    readonly name: 'Meta Ads';
    readonly period_from: string;
    readonly period_to: string;
    readonly collected_at: string | null;
  };
}

// ---------------------------------------------------------------------------
// Load state
// ---------------------------------------------------------------------------

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: CampaignDetailResponse }
  | { kind: 'not-found' }
  | { kind: 'error' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildDetailUrl(id: string, period: string): string {
  return `/ads/campaigns/${encodeURIComponent(id)}?period=${period}`;
}

function periodLabel(period: string): string {
  if (period === '7d') return t('ads.period7d');
  if (period === '90d') return t('ads.period90d');
  if (period === '365d') return t('ads.period365d');
  return t('ads.period30d');
}

// Filtra nulls para o sparkline (Sparkline aceita number[])
function filterSeriesPoints(arr: ReadonlyArray<number | null>): readonly number[] {
  return arr.filter((v): v is number => v !== null);
}

// ---------------------------------------------------------------------------
// Skeleton da grade de KPIs
// ---------------------------------------------------------------------------

function AdsKpiSkeleton(): JSX.Element {
  return (
    <div
      className="ads-kpi-skeleton-grid"
      role="status"
      aria-label="Carregando indicadores da campanha"
      aria-busy="true"
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="ads-kpi-skeleton-card" aria-hidden="true">
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="kpi-value" width="45%" />
          <Skeleton variant="text" width="50%" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card de KPI individual
// ---------------------------------------------------------------------------

interface KpiCardProps {
  readonly label: string;
  readonly value: string;
  readonly isNull: boolean;
  readonly src: string;
  readonly onRefClick: (metric: string) => void;
}

function AdsKpiCard({ label, value, isNull, src, onRefClick }: KpiCardProps): JSX.Element {
  const [showWhy, setShowWhy] = useState(false);

  return (
    <div className="ads-kpi-card">
      <span className="ads-kpi-card-label">{label}</span>
      <span className={`ads-kpi-card-value${isNull ? ' no-data' : ''}`}>{value}</span>

      {isNull ? (
        <div>
          <span className="ads-kpi-no-data-msg">{t('ads.kpiNoData')}</span>{' '}
          <button
            type="button"
            className="ads-kpi-no-data-why"
            onClick={() => setShowWhy((v) => !v)}
            aria-expanded={showWhy}
          >
            {t('ads.kpiNoDataWhy')}
          </button>
          {showWhy ? (
            <div className="ads-kpi-no-data-expand">{t('ads.kpiNoDataExplain')}</div>
          ) : null}
        </div>
      ) : (
        <div className="ads-kpi-card-src">
          {src}{' '}
          <button
            type="button"
            className="ads-ref-chip"
            onClick={() => onRefClick(label)}
            aria-label={`${t('ads.refChipLabel')} — ${label}`}
          >
            [ref:]
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card especial do ROAS (sempre null em F2)
// ---------------------------------------------------------------------------

function AdsRoasCard(): JSX.Element {
  const [showDetail, setShowDetail] = useState(false);

  return (
    <div className="ads-kpi-card">
      <span className="ads-kpi-card-label">{t('ads.kpiRoas')}</span>
      <span className="ads-kpi-card-value no-data">{t('ads.roasNull')}</span>
      <div>
        <span className="ads-roas-null-explain">{t('ads.roasNullExplain')}</span>{' '}
        <button
          type="button"
          className="ads-roas-learn-btn"
          onClick={() => setShowDetail((v) => !v)}
          aria-expanded={showDetail}
        >
          {t('ads.roasNullLearn')}
        </button>
        {showDetail ? <div className="ads-roas-detail-panel">{t('ads.roasNullDetail')}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function AdsCampaignDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [period, setPeriod] = useState<string>(searchParams.get('period') ?? '30d');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [retryKey, setRetryKey] = useState<number>(0);
  const [refPanelData, setRefPanelData] = useState<RefPanelData | null>(null);

  useEffect(() => {
    if (id === undefined || id.length === 0) {
      setState({ kind: 'not-found' });
      return;
    }

    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<CampaignDetailResponse>(buildDetailUrl(id, period));
        if (cancelled) return;
        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) {
            // Backend retorna 404 tanto para "nao existe" quanto para "sem dado no periodo"
            // Wireframe §3.2: distinguir pelo contexto. Como F2 nao tem `no_period_data` flag
            // explicita, exibimos "nao encontrada" como fallback honesto.
            setState({ kind: 'not-found' });
            return;
          }
        }
        setState({ kind: 'error' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, period, retryKey]);

  const handleRefClick = useCallback(
    (metric: string) => {
      if (state.kind !== 'ready') return;
      setRefPanelData({
        metric,
        campaignName: state.data.campaign.name,
        refId: state.data.campaign.ref_id,
        periodFrom: state.data.source.period_from,
        periodTo: state.data.source.period_to,
      });
    },
    [state],
  );

  const handleAskCross = useCallback(() => {
    if (id !== undefined) {
      void navigate(`/chat?context=campaign:${id}`);
    }
  }, [navigate, id]);

  // ---------------------------------------------------------------------------
  // Renders por estado
  // ---------------------------------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <AdsKpiSkeleton />
      </div>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <div className="ads-empty-state">
          <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
            search_off
          </span>
          <h1 className="ads-empty-state-title">{t('ads.notFoundTitle')}</h1>
          <p className="ads-empty-state-desc">{t('ads.notFoundDesc')}</p>
          <div className="ads-empty-state-actions">
            <Link to="/digital/campanhas" className="ads-btn-primary">
              {t('ads.notFoundCta')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <div className="ads-empty-state" role="alert">
          <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
            wifi_off
          </span>
          <h1 className="ads-empty-state-title">{t('ads.errorTitle')}</h1>
          <p className="ads-empty-state-desc">{t('ads.errorDesc')}</p>
          <div className="ads-empty-state-actions">
            <button
              type="button"
              className="ads-btn-primary"
              onClick={() => setRetryKey((k) => k + 1)}
            >
              {t('ads.errorRetry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // state.kind === 'ready'
  // Guarda defensiva: resposta malformada (campaign ausente) cai no estado de erro
  // em vez de lancar TypeError no destructuring.
  const { campaign, source } = state.data;
  if (campaign == null) {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <div className="ads-empty-state" role="alert">
          <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
            wifi_off
          </span>
          <h1 className="ads-empty-state-title">{t('ads.errorTitle')}</h1>
          <p className="ads-empty-state-desc">{t('ads.errorDesc')}</p>
          <div className="ads-empty-state-actions">
            <button
              type="button"
              className="ads-btn-primary"
              onClick={() => setRetryKey((k) => k + 1)}
            >
              {t('ads.errorRetry')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  const { kpis, series } = campaign;

  // Series para sparklines (filtrar nulls)
  const spendPoints = filterSeriesPoints(series.spend);
  const clicksPoints = filterSeriesPoints(series.clicks);
  const cpcPoints = filterSeriesPoints(series.cpc);
  const ctrPoints = filterSeriesPoints(series.ctr);

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="ads-breadcrumb" aria-label="Navegacao">
        <Link to="/digital/campanhas">
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            arrow_back
          </span>
          {t('ads.backToCampaigns')}
        </Link>
      </nav>

      {/* Page header */}
      <div className="ads-page-header">
        <div className="ads-page-eyebrow">
          {t('ads.detailTitle')} · {t('ads.detailSource')}
        </div>
        <h1 className="ads-page-title">{campaign.name}</h1>
        <div className="ads-page-meta">
          <span className="ads-page-meta-item">
            <strong>{t('ads.detailStatus')}:</strong>{' '}
            <span className={`ads-status-badge ${statusCssClass(campaign.status)}`}>
              {statusLabel(campaign.status)}
            </span>
          </span>
          {source.collected_at !== null ? (
            <span className="ads-page-meta-item">
              {t('ads.detailLastCollected')}: {source.collected_at}
            </span>
          ) : null}
        </div>
      </div>

      {/* Filtro de periodo */}
      <div className="ads-filters" role="group" aria-label={t('ads.filterPeriod')}>
        <div className="ads-filter-group">
          <label htmlFor="ads-detail-period-select" className="ads-filter-label">
            {t('ads.filterPeriod')}:
          </label>
          <select
            id="ads-detail-period-select"
            className="ads-filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label={t('ads.filterPeriod')}
          >
            <option value="7d">{t('ads.period7d')}</option>
            <option value="30d">{t('ads.period30d')}</option>
            <option value="90d">{t('ads.period90d')}</option>
            <option value="365d">{t('ads.period365d')}</option>
          </select>
        </div>

        <Link
          to={`/digital/campanhas/compare?ids=${id ?? ''}&period=${period}`}
          className="ads-btn-secondary"
          aria-label={t('ads.compareWith')}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            compare_arrows
          </span>
          {t('ads.compareWith')}
        </Link>
      </div>

      {/* Grade de KPIs */}
      <section aria-labelledby="ads-kpis-title">
        <h2
          id="ads-kpis-title"
          style={{
            fontSize: 'var(--mkv-text-sm)',
            fontWeight: 600,
            color: 'var(--md-on-surface-variant)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 'var(--mkv-space-4)',
          }}
        >
          {t('ads.kpisTitle')}
        </h2>

        <div className="ads-kpi-grid">
          {/* Investimento */}
          <AdsKpiCard
            label={t('ads.kpiSpend')}
            value={fmtCurrency(kpis.spend)}
            isNull={kpis.spend === null}
            src={t('ads.kpiVia')}
            onRefClick={handleRefClick}
          />

          {/* Cliques */}
          <AdsKpiCard
            label={t('ads.kpiClicks')}
            value={fmtNumber(kpis.clicks)}
            isNull={kpis.clicks === null}
            src={t('ads.kpiVia')}
            onRefClick={handleRefClick}
          />

          {/* CPC */}
          <AdsKpiCard
            label={t('ads.kpiCpc')}
            value={fmtCurrency(kpis.cpc)}
            isNull={kpis.cpc === null}
            src={t('ads.kpiCalculated')}
            onRefClick={handleRefClick}
          />

          {/* Impressoes */}
          <AdsKpiCard
            label={t('ads.kpiImpressions')}
            value={fmtNumber(kpis.impressions)}
            isNull={kpis.impressions === null}
            src={t('ads.kpiVia')}
            onRefClick={handleRefClick}
          />

          {/* CTR */}
          <AdsKpiCard
            label={t('ads.kpiCtr')}
            value={fmtPercent(kpis.ctr)}
            isNull={kpis.ctr === null}
            src={t('ads.kpiVia')}
            onRefClick={handleRefClick}
          />

          {/* ROAS — sempre null em F2 (R-063) */}
          <AdsRoasCard />
        </div>
      </section>

      {/* Sparklines da serie historica */}
      {series.dates.length >= 2 ? (
        <section className="ads-sparkline-section" aria-label="Serie historica">
          <div className="ads-sparkline-title">{periodLabel(period)}</div>

          {spendPoints.length >= 2 ? (
            <div className="ads-sparkline-row">
              <span className="ads-sparkline-row-label">{t('ads.kpiSpend')}</span>
              <Sparkline
                points={spendPoints as number[]}
                width={200}
                height={40}
                ariaLabel={`Serie de ${t('ads.kpiSpend')} — ${campaign.name}`}
              />
            </div>
          ) : null}

          {clicksPoints.length >= 2 ? (
            <div className="ads-sparkline-row">
              <span className="ads-sparkline-row-label">{t('ads.kpiClicks')}</span>
              <Sparkline
                points={clicksPoints as number[]}
                width={200}
                height={40}
                ariaLabel={`Serie de ${t('ads.kpiClicks')} — ${campaign.name}`}
              />
            </div>
          ) : null}

          {cpcPoints.length >= 2 ? (
            <div className="ads-sparkline-row">
              <span className="ads-sparkline-row-label">{t('ads.kpiCpc')}</span>
              <Sparkline
                points={cpcPoints as number[]}
                width={200}
                height={40}
                ariaLabel={`Serie de ${t('ads.kpiCpc')} — ${campaign.name}`}
              />
            </div>
          ) : null}

          {ctrPoints.length >= 2 ? (
            <div className="ads-sparkline-row">
              <span className="ads-sparkline-row-label">{t('ads.kpiCtr')}</span>
              <Sparkline
                points={ctrPoints as number[]}
                width={200}
                height={40}
                ariaLabel={`Serie de ${t('ads.kpiCtr')} — ${campaign.name}`}
              />
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Bloco CROSS insight — so renderiza quando nao for null */}
      {campaign.campaign_insight !== null && campaign.campaign_insight.length > 0 ? (
        <section className="ads-cross-insight" aria-labelledby="ads-cross-insight-title">
          <div className="ads-cross-insight-title" id="ads-cross-insight-title">
            {t('ads.crossInsightTitle')}
          </div>
          <p className="ads-cross-insight-text">{campaign.campaign_insight}</p>
          <div className="ads-cross-insight-actions">
            <button
              type="button"
              className="ads-ref-chip"
              style={{ display: 'inline-flex' }}
              aria-label={`${t('ads.refPanelTitle')} — ${campaign.name}`}
              onClick={() => handleRefClick(t('ads.crossInsightTitle'))}
            >
              [ref: Meta Ads · {source.period_from}–{source.period_to}]
            </button>
            <button type="button" className="ads-cross-insight-ask" onClick={handleAskCross}>
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 14 }}
                aria-hidden="true"
              >
                forum
              </span>
              {t('ads.crossAskMore')}
            </button>
          </div>
        </section>
      ) : null}

      {/* FAB — Perguntar ao CROSS sobre esta campanha */}
      <button
        type="button"
        className="ads-fab"
        onClick={handleAskCross}
        aria-label={t('ads.askCross')}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          forum
        </span>
        {t('ads.askCross')}
      </button>

      {/* Painel de rastreabilidade */}
      {refPanelData !== null ? (
        <AdsRefPanel data={refPanelData} onClose={() => setRefPanelData(null)} />
      ) : null}
    </div>
  );
}

// Exporta tipos necessarios pelas outras telas
export type { CampaignDetailResponse, CampaignSeries, CampaignDetailItem };
